import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Crown, History, ShieldCheck, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, Empty } from "@/components/Common";
import { PersonCard } from "@/components/people/PersonCard";
import { Progress, SearchForm } from "@/components/people/SearchForm";
import { CompanyIntel, Filters, ReverseApply, applyFilters } from "@/components/people/CompanyIntel";
import { ResumeEmailDialog } from "@/components/people/ResumeEmailDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, isUpgrade, tierRank } from "@/lib/api";
import { worksAt } from "@/lib/peopleFinder";

const BLANK = { company_name: "", role_keywords: "", person_name: "", location_hint: "", intent: "job_search", max_people: 5, job_id: "", job_title: "" };
const clean = ({ job_title, ...f }) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === "string" ? v.trim() || null : v]).filter(([k, v]) => v !== null || k === "company_name"));
const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const HistoryList = ({ items, onPick, busy }) => (
  <Card className="h-fit space-y-2 self-start" data-testid="people-history">
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500"><History className="h-3.5 w-3.5" />Recent searches</p>
    {!items.length ? <p className="text-xs text-zinc-600">Your searches will show here.</p> : items.map((h, i) => (
      <button key={i} disabled={busy} onClick={() => onPick(h)} className="block w-full rounded-lg px-2 py-1.5 text-left hover:bg-[#18181B] disabled:opacity-50" data-testid={`people-history-item-${i}`}>
        <span className="block truncate text-sm text-zinc-200">{h.company_name}{h.role_keywords ? ` · ${h.role_keywords}` : ""}{h.person_name ? ` · ${h.person_name}` : ""}</span>
        <span className="block truncate text-[11px] text-zinc-500">{h.people ?? 0} people{h.job_title ? ` · for ${h.job_title}` : ""} · {fmt(h.at || h.timestamp)}</span>
      </button>
    ))}
  </Card>
);

const Locked = () => (
  <Empty icon={Crown} title="People Finder is an Elite feature" text="Find hiring managers and recruiters at any company, with likely emails and outreach drafts written for you.">
    <Button className="mt-4" onClick={() => window.dispatchEvent(new CustomEvent("rpopro:upgrade", { detail: { message: "People Finder is an Elite feature", tier: "elite" } }))} data-testid="people-upgrade-button"><Crown className="h-4 w-4" />Unlock with Elite</Button>
  </Empty>
);

const TrackerMatches = ({ items }) => !items?.length ? null : (
  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400" data-testid="people-tracker-matches">
    <Briefcase className="h-3.5 w-3.5 text-[#D4AF37]" />In your Tracker:
    {items.map((j) => <span key={j.job_id} className="rounded-full border border-[#27272A] px-2 py-0.5 text-zinc-300">{j.title} · <span className="capitalize">{j.status}</span> · {j.contacts || 0} contacts</span>)}
  </div>
);

export default function PeopleFinder() {
  const { tier } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [locked, setLocked] = useState(tierRank(tier) < 2);
  const [history, setHistory] = useState([]);
  const [missing, setMissing] = useState(false);
  const [filters, setFilters] = useState([]);
  const [draft, setDraft] = useState(null);
  const auto = useRef(false);

  const loadHistory = () => api.get("/people-finder/history").then((r) => setHistory(r.data?.items || [])).catch(() => {});
  useEffect(() => { if (!locked) loadHistory(); }, [locked]);
  useEffect(() => { setLocked(tierRank(tier) < 2); }, [tier]);

  const run = async (f = form) => {
    setBusy(true); setRes(null); setFilters([]); setMissing(false);
    try { const { data } = await api.post("/people-finder/search", clean(f), { timeout: 120000 }); setRes(data); loadHistory(); }
    catch (e) { if (isUpgrade(e)) setLocked(true); else if (e.response?.status === 404) setMissing(true); else if (e.response?.status === 429) toast.error("You've hit today's limit of 20 new searches. Repeat searches from the last 24 hours are still free."); else toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (auto.current || !sp.get("company")) return;
    auto.current = true;
    const f = { ...BLANK, company_name: sp.get("company"), role_keywords: sp.get("role") || "", job_id: sp.get("job_id") || "", job_title: sp.get("job_title") || "" };
    setForm(f);
    if (sp.get("run") === "1" && !locked) { run(f); setSp((p) => { p.delete("run"); return p; }, { replace: true }); }
  }, [sp, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (h) => { const f = { ...BLANK, ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, h[k] ?? BLANK[k]])) }; setForm(f); run(f); };
  const reTitle = (t) => { const f = { ...form, role_keywords: t, person_name: "" }; setForm(f); run(f); };
  const unlinkJob = () => setForm((f) => ({ ...f, job_id: "", job_title: "" }));

  const meta = res?.search_metadata;
  const target = meta?.company_name || form.company_name;
  const atCompany = (res?.people || []).filter((p) => worksAt(p, target, res?.company_intel?.domain));
  const hidden = (res?.people?.length || 0) - atCompany.length;
  const people = applyFilters(atCompany, filters);
  const ctx = { company: target, matches: res?.tracker_matches, job_id: meta?.job_id || form.job_id, onSaved: () => {} };
  return (
    <div className="space-y-6" data-testid="people-finder-page">
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Elite</span>
        <h1 className="font-display text-4xl text-zinc-50 sm:text-5xl">People Finder</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-400">Find the hiring managers and recruiters at a company, get company intel and a pitch, with likely emails and outreach written for you.</p>
        {!locked && <Link to="/people-finder/contacts" className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 px-3 py-1 text-xs text-[#F3C649] hover:bg-[#D4AF37]/10" data-testid="people-all-contacts-link"><Users className="h-3.5 w-3.5" />My contacts</Link>}
      </div>
      {locked ? <Locked /> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
          <div className="min-w-0 space-y-4">
            {form.job_id && <p className="flex flex-wrap items-center gap-2 text-xs text-zinc-400" data-testid="people-job-context"><Briefcase className="h-3.5 w-3.5 text-[#D4AF37]" />Tailored to {form.job_title || "your job"}<button onClick={unlinkJob} className="text-zinc-500 hover:text-zinc-200" data-testid="people-job-unlink"><X className="h-3.5 w-3.5" /></button></p>}
            <SearchForm value={form} onChange={setForm} onSubmit={() => run()} busy={busy} />
            {busy && <Progress />}
            {missing && !busy && <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="people-backend-pending">People Finder is almost ready. It goes live as soon as the server update is published.</p>}
            {res && <>
              <CompanyIntel c={res.company_intel} />
              <ReverseApply r={res.reverse_apply} onTitle={reTitle} />
              <TrackerMatches items={res.tracker_matches} />
              {(meta?.filtered_out > 0 || hidden > 0) && <p className="flex items-center gap-1.5 text-xs text-zinc-500" data-testid="people-hidden-note"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />Removed {(meta?.filtered_out || 0) + hidden} former or look-alike {(meta?.filtered_out || 0) + hidden === 1 ? "match" : "matches"}. Only people who currently work at {target} are shown.</p>}
              {atCompany.length ? <>
                <Filters on={filters} setOn={setFilters} />
                <p className="text-xs text-zinc-500" data-testid="people-count">{people.length} of {atCompany.length} people at {ctx.company}{meta?.domain_mx_valid === false ? " · this domain has no mail server, so email guesses are less likely" : ""}</p>
                {meta?.email_note && <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="people-email-note">{meta.email_note}</p>}
                {people.length ? <div className="space-y-3" data-testid="people-results">{people.map((p, i) => <PersonCard key={`${p.full_name}-${i}`} p={p} i={i} ctx={ctx} onResume={(d) => setDraft({ ...d, job_id: ctx.job_id || null })} />)}</div>
                  : <p className="text-sm text-zinc-500" data-testid="people-filter-empty">No one matches these filters.</p>}
              </> : <Empty icon={Users} title={`Nobody found at ${target}`} text="Try a different role keyword or the company's exact name." />}
            </>}
          </div>
          <HistoryList items={history} onPick={pick} busy={busy} />
        </div>
      )}
      <ResumeEmailDialog draft={draft} onClose={() => setDraft(null)} />
    </div>
  );
}
